import { useEffect } from 'react';

/**
 * Listens for Enter key while active and, when condition is met, calls onClose.
 * Uses document capture phase for consistency with useEscapeToClose.
 * Use when Enter should commit/save and close the editor (e.g. date picker, single-select).
 *
 * @param isActive - Whether the listener is active (e.g. cell is editing).
 * @param onClose - Called when Enter is pressed and shouldClose is true.
 * @param shouldClose - When true, Enter will call onClose (e.g. validation passed). Default true.
 */
export function useEnterToClose(
  isActive: boolean,
  onClose?: () => void,
  shouldClose: boolean = true
): void {
  useEffect(() => {
    if (!isActive || !onClose || !shouldClose) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isActive, onClose, shouldClose]);
}
